import {
  EditRounded as EditIcon,
  ExposureRounded as DiffIcon,
} from "@mui/icons-material";
import {
  Alert,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Component from "components/Component";
import CodeEditor, {
  DiffEditor,
  EditorPreferenceToggle,
} from "components/admin/CodeEditor";
import BigLoader from "components/common/BigLoader";
import Button from "components/common/Button";
import CopyButton from "components/common/CopyButton";
import TextField from "components/common/TextField";
import * as config from "config";
import { detailedDiff } from "deep-object-diff";
import { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import * as adminService from "services/adminService";
import * as userService from "services/userService";
import * as adminActions from "store/actions/temp/admin/adminActions";
import * as adminSelectors from "store/selectors/temp/admin/adminSelectors";
import * as utils from "utils";

const diff = (oldJson, candidateJsonText) => {
  try {
    return {
      hasError: false,
      diff: detailedDiff(oldJson, JSON.parse(candidateJsonText)),
    };
  } catch (e) {
    return {
      hasError: true,
      message: `${e.name} ${e.message}`,
    };
  }
};

const ConfigDiff = ({
  data,
  newData,
  companyId,
  postSaveCallback,
  saveUserLogMessage,
  userLogMessage,
  hasUserLogMessage,
}) => {
  const [
    updateUserConfig,
    { isLoading, isError },
  ] = adminService.useUpdateUserConfigMutation();

  const diffResult = diff(data, newData);
  const hasChanges =
    !diffResult.hasError &&
    (Object.keys(diffResult.diff?.added ?? {}).length > 0 ||
      Object.keys(diffResult.diff?.deleted ?? {}).length > 0 ||
      Object.keys(diffResult.diff?.updated ?? {}).length > 0);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {diffResult.hasError ? (
          <Alert severity={"error"}>{diffResult.message}</Alert>
        ) : !hasChanges ? (
          <Alert severity={"info"}>{"No changes detected"}</Alert>
        ) : (
          <DiffEditor
            original={JSON.stringify(data, null, 2)}
            modified={newData}
            language={"json"}
          />
        )}
      </Grid>
      {hasChanges && (
        <>
          <Grid item xs={10}>
            <TextField
              variant={"filled"}
              label={"Reason for change"}
              fullWidth
              onChange={(e) => saveUserLogMessage(e.target.value)}
              value={userLogMessage}
            />
          </Grid>
          <Grid item xs={2}>
            <Grid
              container
              justifyContent={"flex-end"}
              alignItems={"center"}
              style={{ height: "100%" }}
            >
              <Button
                variant={"contained"}
                disableElevation
                color={"primary"}
                disabled={!hasUserLogMessage || isLoading}
                isLoading={isLoading}
                hasError={isError}
                errorTooltip={"Failed to save"}
                onClick={async () => {
                  try {
                    await updateUserConfig({
                      companyId,
                      userLogMessage,
                      value: JSON.parse(newData),
                    });
                    postSaveCallback();
                  } catch {
                    // Error handled by mutation state (isError)
                  }
                }}
              >
                {"Save Changes"}
              </Button>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};

const UserConfigRawEditor = ({
  saveUserLogMessage,
  userLogMessage,
  hasUserLogMessage,
}) => {
  const { companyId } = useParams();

  const {
    currentData: userConfig,
    isLoading: isUserConfigLoading,
    isError: isUserConfigError,
    refetch: refetchUserConfig,
  } = userService.useRetrieveConfigQuery({ companyId }, { skip: !companyId });

  const [newConfigData, setNewConfigData] = useState(null);
  const [differenceToggle, setDifferenceToggle] = useState(false);
  const baselineRef = useRef(null);
  const [isStale, setIsStale] = useState(false);

  // Reset when company changes
  useEffect(() => {
    setNewConfigData(null);
    setDifferenceToggle(false);
    baselineRef.current = null;
    setIsStale(false);
  }, [companyId]);

  // Snapshot baseline when data first loads
  useEffect(() => {
    if (userConfig && baselineRef.current === null) {
      baselineRef.current = JSON.stringify(userConfig);
    }
  }, [userConfig]);

  // Poll for external changes without updating editor data
  useEffect(() => {
    if (!companyId || isStale) return;
    const interval = setInterval(async () => {
      try {
        const response = await utils.authenticatedFetch(
          config.endpoints.user(`user-config/${companyId}`),
          {}
        );
        const data = await response.json();
        if (
          baselineRef.current !== null &&
          JSON.stringify(data) !== baselineRef.current
        ) {
          setIsStale(true);
        }
      } catch {
        // Silently ignore poll failures
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [companyId, isStale]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.metaKey && event.key === "d") {
        event.preventDefault();
        if (!differenceToggle) setDifferenceToggle(true);
      } else if (event.metaKey && event.key === "e") {
        event.preventDefault();
        if (differenceToggle) setDifferenceToggle(false);
      }
    },
    [differenceToggle]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const loadedData = userConfig ?? {};
  const jsonValue = newConfigData ?? JSON.stringify(loadedData, null, 2);

  return (
    <Component
      title={"Company Overlay Config"}
      subtitle={
        "Edit the company overlay config directly. This includes componentVisibility, defaultJudgmentNames, judgmentOptions, and other settings."
      }
      spacing={config.GRID_SPACING}
    >
      {isUserConfigLoading ? (
        <BigLoader />
      ) : isUserConfigError ? (
        <Alert severity={"error"}>{"Failed to load user config."}</Alert>
      ) : (
        <Grid container spacing={2}>
          {isStale && (
            <Grid item xs={12}>
              <Alert
                severity={"warning"}
                action={
                  <Button
                    color={"inherit"}
                    size={"small"}
                    onClick={() => window.location.reload()}
                  >
                    {"Refresh"}
                  </Button>
                }
              >
                {
                  "This config has been updated by another user. Refresh to see the latest version."
                }
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <ToggleButtonGroup
                value={differenceToggle ? "Diff" : "Edit"}
                onChange={() => setDifferenceToggle(!differenceToggle)}
                aria-label={"Toggle between edit and diff modes (⌘+E / ⌘+D)"}
                color={"primary"}
                size={"small"}
                exclusive
              >
                <ToggleButton value={"Edit"} title={"Edit mode (⌘+E)"}>
                  <EditIcon fontSize={"small"} sx={{ marginRight: "3px" }} />
                  <span style={{ paddingRight: "3px" }}>{"Edit"}</span>
                </ToggleButton>
                <ToggleButton value={"Diff"} title={"Diff mode (⌘+D)"}>
                  <DiffIcon fontSize={"small"} sx={{ marginRight: "3px" }} />
                  <span style={{ paddingRight: "2px" }}>{"Diff"}</span>
                </ToggleButton>
              </ToggleButtonGroup>
              {!differenceToggle &&
                newConfigData != null &&
                newConfigData !== JSON.stringify(loadedData, null, 2) && (
                  <Button
                    variant={"contained"}
                    disableElevation
                    color={"primary"}
                    onClick={() =>
                      setNewConfigData(JSON.stringify(loadedData, null, 2))
                    }
                  >
                    {"Undo edits"}
                  </Button>
                )}
              <Stack direction={"row"} spacing={1}>
                <CopyButton content={jsonValue} />
                <EditorPreferenceToggle />
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            {differenceToggle ? (
              <ConfigDiff
                data={loadedData}
                newData={newConfigData ?? JSON.stringify(loadedData, null, 2)}
                companyId={companyId}
                postSaveCallback={async () => {
                  setNewConfigData(null);
                  baselineRef.current = null;
                  setIsStale(false);
                  await refetchUserConfig();
                  setDifferenceToggle(false);
                }}
                saveUserLogMessage={saveUserLogMessage}
                userLogMessage={userLogMessage}
                hasUserLogMessage={hasUserLogMessage}
              />
            ) : (
              <CodeEditor
                value={jsonValue}
                language={"json"}
                onChange={(value) => setNewConfigData(value)}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Component>
  );
};

const mapStateToProps = (state) => {
  return {
    userLogMessage: adminSelectors.userLogMessage(state),
    hasUserLogMessage: adminSelectors.hasUserLogMessage(state),
  };
};

const mapDispatchToProps = {
  saveUserLogMessage: adminActions.userLogMessage.update,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UserConfigRawEditor);
